import React, { useState, useEffect, useMemo } from "react";
import {
  getDistricts,
  getNeighborhoods,
} from "../services/addressService";
import { getAdresAnalizi } from "../services/adresAnaliziService";
import { SearchIcon } from "../assets/icons";
import Pagination from "../components/Pagination";
import { MultiSelect } from "primereact/multiselect";
import { formatDate } from "../helpers/stringHelpers";
import "../styles/pages/_mahalle-analizi.scss";

const PAGE_SIZE_OPTIONS = [50, 100, 500, 1000, "HEPSİ"];
const ISLEM_TURU_OPTIONS = [
  "Atık Alındı",
  "Poşet Verildi",
  "Poşet Dolmamış",
  "Projeye Katılmıyor",
  "Gidilmedi",
];

// Gidilmedi = sadece islemTuru === "Gidilmedi" olanlar
const isGidilen = (record) =>
  String(record.islemTuru || "").trim() !== "Gidilmedi";

// Veriyi mahalle -> sokak -> apartman hierarchy'sine grupla
function groupByHierarchy(data) {
  const byMahalle = {};
  for (const r of data) {
    const mah = r.mahalle || "-";
    const sok = r.sokak || "-";
    const apt = r.apartmanNo || "-";
    if (!byMahalle[mah]) {
      byMahalle[mah] = { sokaklar: {}, records: [], gidilen: 0, gidilmeyen: 0 };
    }
    const g = isGidilen(r) ? 1 : 0;
    byMahalle[mah].gidilen += g;
    byMahalle[mah].gidilmeyen += 1 - g;
    byMahalle[mah].records.push(r);

    if (!byMahalle[mah].sokaklar[sok]) {
      byMahalle[mah].sokaklar[sok] = {
        apartmanlar: {},
        records: [],
        gidilen: 0,
        gidilmeyen: 0,
      };
    }
    byMahalle[mah].sokaklar[sok].gidilen += g;
    byMahalle[mah].sokaklar[sok].gidilmeyen += 1 - g;
    byMahalle[mah].sokaklar[sok].records.push(r);

    if (!byMahalle[mah].sokaklar[sok].apartmanlar[apt]) {
      byMahalle[mah].sokaklar[sok].apartmanlar[apt] = {
        records: [],
        gidilen: 0,
        gidilmeyen: 0,
      };
    }
    byMahalle[mah].sokaklar[sok].apartmanlar[apt].gidilen += g;
    byMahalle[mah].sokaklar[sok].apartmanlar[apt].gidilmeyen += 1 - g;
    byMahalle[mah].sokaklar[sok].apartmanlar[apt].records.push(r);
  }
  return byMahalle;
}

export default function MahalleAnalizi() {
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);

  const [ilce, setIlce] = useState("");
  const [selectedMahalleler, setSelectedMahalleler] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [islemTurleri, setIslemTurleri] = useState([]);
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [responseData, setResponseData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]); // can be number or "HEPSİ"

  const [collapsedMahalle, setCollapsedMahalle] = useState(new Set());
  const [collapsedSokak, setCollapsedSokak] = useState(new Set());
  const [collapsedApartman, setCollapsedApartman] = useState(new Set());

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    fetchDistricts();
  }, []);

  useEffect(() => {
    if (ilce) {
      fetchNeighborhoods(ilce);
    } else {
      setNeighborhoods([]);
      setSelectedMahalleler([]);
    }
  }, [ilce]);

  const fetchDistricts = async () => {
    try {
      setLoadingDistricts(true);
      const res = await getDistricts();
      if (res?.status === 200 && res?.data) {
        setDistricts(res.data);
      }
    } catch (err) {
      console.error("İlçeler yüklenemedi:", err);
    } finally {
      setLoadingDistricts(false);
    }
  };

  const fetchNeighborhoods = async (districtId) => {
    try {
      setLoadingNeighborhoods(true);
      const res = await getNeighborhoods(districtId);
      if (res?.status === 200 && res?.data) {
        setNeighborhoods(res.data);
      }
    } catch (err) {
      console.error("Mahalleler yüklenemedi:", err);
      setNeighborhoods([]);
    } finally {
      setLoadingNeighborhoods(false);
    }
  };

  const buildMahalleler = () =>
    selectedMahalleler.length > 0
      ? selectedMahalleler.map((m) => {
          if (typeof m === "object" && m.value) return m.value.ad;
          if (typeof m === "object" && m.ad) return m.ad;
          return String(m);
        })
      : undefined;

  const fetchData = async (overrides = {}) => {
    const pNum = overrides.pageNumber ?? pageNumber;
    const pSize = overrides.pageSize ?? pageSize;

    if (!startDate || !endDate) {
      setError("Başlangıç ve bitiş tarihleri zorunludur.");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Başlangıç tarihi bitiş tarihinden büyük olamaz.");
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const Basla = `${startDate}T00:00:00.000Z`;
      const Bitir = `${endDate}T23:59:59.999Z`;
      const districtObj = districts.find((d) => d.id === ilce);
      const Ilce = districtObj ? districtObj.ad : undefined;
      const Mahalleler = buildMahalleler();

      const pageSizeParam =
        pSize === "HEPSİ" ? 20000000 : Number(pSize || PAGE_SIZE_OPTIONS[0]);

      const res = await getAdresAnalizi({
        Basla,
        Bitir,
        Ilce,
        Mahalleler,
        IslemTurleri: islemTurleri,
        Search: search.trim() || undefined,
        PageNumber: pNum,
        PageSize: pageSizeParam,
      });

      const data = res?.data ?? [];
      setResponseData(Array.isArray(data) ? data : []);
      setTotalRecords(res?.totalRecords ?? 0);
      setPageNumber(res?.pageNumber ?? pNum);
      setHasSearched(true);
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Veri yüklenemedi."
      );
      setResponseData([]);
      setTotalRecords(0);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAra = () => {
    setPageNumber(1);
    fetchData({ pageNumber: 1 });
  };

  const handlePageChange = (newPage) => {
    const nextPage = newPage + 1;
    setPageNumber(nextPage);
    fetchData({ pageNumber: nextPage });
  };

  const handlePageSizeChange = (e) => {
    const raw = e.target.value;
    const size = raw === "HEPSİ" ? "HEPSİ" : Number(raw);
    setPageSize(size);
    setPageNumber(1);
    fetchData({ pageNumber: 1, pageSize: size });
  };

  const totalPages = useMemo(() => {
    if (!totalRecords) return 1;
    if (pageSize === "HEPSİ") return 1;
    const numeric = Number(pageSize || PAGE_SIZE_OPTIONS[0]);
    return Math.max(1, Math.ceil(totalRecords / numeric));
  }, [totalRecords, pageSize]);

  // Gidilme Durumu: frontend-only filter (no API re-fetch)
  const groupedData = useMemo(
    () => groupByHierarchy(responseData),
    [responseData]
  );

  // Katılmama sebebine göre sayım (boş olanlar hariç), çoktan aza sıralı
  const katilmamaSebebiCounts = useMemo(() => {
    const counts = {};
    for (const rec of responseData) {
      const sebep = String(rec.katilmamaSebebi || "").trim();
      if (!sebep) continue;
      counts[sebep] = (counts[sebep] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [responseData]);

  // Start all panels collapsed when groupedData changes
  useEffect(() => {
    const mahKeys = new Set();
    const sokKeys = new Set();
    const aptKeys = new Set();
    Object.entries(groupedData).forEach(([mah, mahData]) => {
      mahKeys.add(mah);
      Object.entries(mahData.sokaklar || {}).forEach(([sok, sokData]) => {
        sokKeys.add(`${mah}__${sok}`);
        Object.keys(sokData.apartmanlar || {}).forEach((apt) => {
          aptKeys.add(`${mah}__${sok}__${apt}`);
        });
      });
    });
    setCollapsedMahalle(mahKeys);
    setCollapsedSokak(sokKeys);
    setCollapsedApartman(aptKeys);
  }, [groupedData]);

  const toggleApartman = (key) => {
    setCollapsedApartman((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleMahalle = (key) => {
    setCollapsedMahalle((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSokak = (key) => {
    setCollapsedSokak((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") handleAra();
  };

  return (
    <div className="mahalle-analizi-container people-container">
      <h1 className="page-title">Mahalle Analizi</h1>

      {error && <div className="error-message">{error}</div>}

      <div className="search-container mahalle-analizi-filters">
        <div className="search-inputs filter-row">
          <div className="search-input-group">
            <label>İlçe</label>
            <select
              value={ilce}
              onChange={(e) => setIlce(e.target.value)}
              className="search-select"
              disabled={loadingDistricts}
            >
              <option value="">İlçe Seçiniz</option>
              {districts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.ad}
                </option>
              ))}
            </select>
          </div>

          <div className="search-input-group">
            <label>Mahalle (Çoklu)</label>
            <MultiSelect
              value={selectedMahalleler}
              options={neighborhoods.map((n) => ({ label: n.ad, value: n }))}
              onChange={(e) => setSelectedMahalleler(e.value || [])}
              optionLabel="label"
              placeholder="Mahalle Seçiniz"
              className="search-select mahalle-multiselect"
              disabled={loadingNeighborhoods || !ilce}
              filter
              display="chip"
              maxSelectedLabels={3}
            />
          </div>

          <div className="search-input-group">
            <label>Başlangıç Tarihi *</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="search-date-input"
            />
          </div>

          <div className="search-input-group">
            <label>Bitiş Tarihi *</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="search-date-input"
            />
          </div>

          <div className="search-input-group">
            <label>İşlem Türü (Çoklu)</label>
            <MultiSelect
              value={islemTurleri}
              options={ISLEM_TURU_OPTIONS}
              onChange={(e) => setIslemTurleri(e.value || [])}
              placeholder="İşlem Türü"
              className="search-select"
              filter
              display="chip"
            />
          </div>

          {/* <div className="search-input-group">
            <label>Gidilme Durumu</label>
            <select
              value={gidildiMi === null ? "" : gidildiMi ? "true" : "false"}
              onChange={(e) => {
                const v = e.target.value;
                setGidildiMi(v === "" ? null : v === "true");
              }}
              className="search-select"
            >
              <option value="">Tümü</option>
              <option value="true">Gidilen</option>
              <option value="false">Gidilmeyen</option>
            </select>
          </div> */}

          <div className="search-input-group">
            <label>Ara</label>
            <div className="search-input-with-btn">
              <input
                type="text"
                placeholder="Personel, adres, açıklama..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="search-input"
              />
              <button onClick={handleAra} className="search-button">
                <SearchIcon className="search-icon" />
              </button>
            </div>
          </div>
        </div>

        <div className="filter-actions">
          <button
            onClick={handleAra}
            className="filter-button ara-button"
            disabled={loading || !startDate || !endDate}
          >
            {loading ? "Yükleniyor..." : "Ara"}
          </button>
        </div>
      </div>

      {responseData.length > 0 && (
        <div className="mahalle-analizi-results">
          <div className="pagination-controls">
            <div className="page-size-select">
              <label>Sayfa Boyutu:</label>
              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                className="page-size-select-input"
              >
                {PAGE_SIZE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="total-info">
              Toplam {totalRecords} kayıt
            </div>
          </div>

          {katilmamaSebebiCounts.length > 0 && (
            <div className="katilmama-sebebi-summary">
              <div className="katilmama-sebebi-title">Katılmama Sebebi Dağılımı</div>
              <div className="katilmama-sebebi-list">
                {katilmamaSebebiCounts.map(([sebep, count]) => (
                  <div key={sebep} className="katilmama-sebebi-item">
                    <span className="katilmama-sebebi-label">{sebep}</span>
                    <span className="katilmama-sebebi-count">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="accordion-list">
            {Object.entries(groupedData).map(([mahalleAd, mahData]) => {
              const mahKey = mahalleAd;
              const isMahOpen = !collapsedMahalle.has(mahKey);
              return (
                <div key={mahKey} className="accordion-panel mahalle-panel">
                  <div
                    className={`panel-header mahalle-header ${isMahOpen ? "open" : ""}`}
                    onClick={() => toggleMahalle(mahKey)}
                  >
                    <span className="collapse-icon">{isMahOpen ? "▼" : "▶"}</span>
                    <span className="panel-title">Mahalle: {mahalleAd}</span>
                    <span className="panel-stats">
                      <span className="stat-gidilen">Gidilen: {mahData.gidilen}</span>
                      <span className="stat-gidilmeyen">
                        Gidilmeyen: {mahData.gidilmeyen}
                      </span>
                    </span>
                  </div>

                  {isMahOpen && (
                    <div className="panel-content">
                      {Object.entries(mahData.sokaklar).map(([sokakAd, sokData]) => {
                        const sokKey = `${mahKey}__${sokakAd}`;
                        const isSokOpen = !collapsedSokak.has(sokKey);
                        return (
                          <div key={sokKey} className="accordion-panel sokak-panel">
                            <div
                              className={`panel-header sokak-header ${isSokOpen ? "open" : ""}`}
                              onClick={() => toggleSokak(sokKey)}
                            >
                              <span className="collapse-icon">
                                {isSokOpen ? "▼" : "▶"}
                              </span>
                              <span className="panel-title">Sokak: {sokakAd}</span>
                              <span className="panel-stats">
                                <span className="stat-gidilen">
                                  Gidilen: {sokData.gidilen}
                                </span>
                                <span className="stat-gidilmeyen">
                                  Gidilmeyen: {sokData.gidilmeyen}
                                </span>
                              </span>
                            </div>

                            {isSokOpen && (
                              <div className="panel-content">
                                {Object.entries(sokData.apartmanlar).map(
                                  ([aptNo, aptData]) => {
                                    const aptKey = `${mahKey}__${sokakAd}__${aptNo}`;
                                    const isAptOpen = !collapsedApartman.has(aptKey);
                                    return (
                                    <div
                                      key={aptKey}
                                      className="accordion-panel apartman-panel"
                                    >
                                      <div
                                        className={`panel-header apartman-header ${isAptOpen ? "open" : ""}`}
                                        onClick={() => toggleApartman(aptKey)}
                                      >
                                        <span className="collapse-icon">
                                          {isAptOpen ? "▼" : "▶"}
                                        </span>
                                        <span className="panel-title">
                                          Apartman: {aptNo}
                                        </span>
                                        <span className="panel-stats">
                                          <span className="stat-gidilen">
                                            Gidilen: {aptData.gidilen}
                                          </span>
                                          <span className="stat-gidilmeyen">
                                            Gidilmeyen: {aptData.gidilmeyen}
                                          </span>
                                        </span>
                                      </div>
                                      {isAptOpen && (
                                      <div className="panel-content">
                                        <table className="mahalle-analizi-table">
                                          <thead>
                                            <tr>
                                              <th>İşlem QR</th>
                                              <th>İşlem Tarihi</th>
                                              <th>İşlem Saati</th>
                                              <th>Kapı No</th>
                                              <th>Personel</th>
                                              <th>İşlem Türü</th>
                                              <th>Açıklama</th>
                                              <th>Katılmama Sebebi</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {aptData.records.map((rec, idx) => (
                                              <tr
                                                key={rec.islemQr || idx}
                                                className={
                                                  isGidilen(rec)
                                                    ? "row-gidilen"
                                                    : "row-gidilmeyen"
                                                }
                                              >
                                                <td>{rec.islemQr}</td>
                                                <td>{formatDate(rec.islemTarihi)}</td>
                                                <td>{rec.islemSaati || "-"}</td>
                                                <td>{rec.kapiNo || "-"}</td>
                                                <td>{rec.personel || "-"}</td>
                                                <td>{rec.islemTuru || "-"}</td>
                                                <td>{rec.aciklama || "-"}</td>
                                                <td>{rec.katilmamaSebebi || "-"}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                      )}
                                    </div>
                                    );
                                  }
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Pagination
            currentPage={pageNumber - 1}
            totalPages={totalPages}
            onPageChange={(p) => handlePageChange(p)}
          />
        </div>
      )}

      {!loading && responseData.length === 0 && totalRecords === 0 && (
        <div className="empty-state">
          {hasSearched
            ? "Belirtilen kriterlere uygun sonuç bulunamadı."
            : "Başlangıç ve bitiş tarihlerini seçip Ara butonuna basarak sonuçları görüntüleyebilirsiniz."}
        </div>
      )}
    </div>
  );
}
